// src/components/SettingsScreen.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  ArrowBack,
  StorageRounded,
  DeleteForever,
  Refresh,
  DarkMode,
  DataUsage,
  Info,
  BuildCircle,
  CheckCircleOutline,
  CloudDownload,
  ExpandMore,
  Check,
  Error as ErrorIcon,
  Storage
} from '@mui/icons-material';
import DocumentService from '../services/DocumentService';
import AutoCacheService from '../services/AutoCacheService';
import BandwidthLimitedDownloadService from '../services/BandwidthLimitedDownloadService';
import { useTheme } from '../contexts/ThemeContext';

const SettingsScreen = ({ onBack, onTriggerSync }) => {
  // Get the theme toggle from context
  const { darkMode, toggleDarkMode } = useTheme();
  
  const [autoSync, setAutoSync] = useState(true);
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [autoCaching, setAutoCaching] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [appVersion, setAppVersion] = useState('1.0.0');
  const [offlineStats, setOfflineStats] = useState({ documents: 0, sections: 0 });
  const [loading, setLoading] = useState(true);
  
  // State variables for bandwidth usage
  const [bandwidthUsage, setBandwidthUsage] = useState(0);
  const [bandwidthLimit] = useState(BandwidthLimitedDownloadService.DAILY_BACKGROUND_LIMIT);

  // New state for storage diagnostics
  const [testResults, setTestResults] = useState(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [diagnosticsError, setDiagnosticsError] = useState(null);

  // Load offline stats when component mounts
  useEffect(() => {
    const loadOfflineStats = async () => {
      try {
        const offlineDocs = await DocumentService.getOfflineDocuments();
        
        // Count sections
        const sections = new Set();
        offlineDocs.forEach(doc => {
          if (doc.section) {
            sections.add(doc.section);
          }
        });
        
        setOfflineStats({
          documents: offlineDocs.length,
          sections: sections.size
        });
      } catch (error) {
        console.error('Error loading offline stats:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadOfflineStats();
    
    // Load auto-update preference
    const savedAutoUpdate = localStorage.getItem('autoUpdate');
    if (savedAutoUpdate !== null) {
      setAutoUpdate(JSON.parse(savedAutoUpdate));
    }
    
    // Load auto-sync preference
    const savedAutoSync = localStorage.getItem('autoSync');
    if (savedAutoSync !== null) {
      setAutoSync(JSON.parse(savedAutoSync));
    }
    
    // Load auto-caching preference
    const savedAutoCaching = localStorage.getItem('autoCaching');
    if (savedAutoCaching !== null) {
      setAutoCaching(savedAutoCaching === 'true');
    }

    // Run storage diagnostics
    runDiagnostics();
  }, []);

  // Load bandwidth usage
  useEffect(() => {
    // Get current bandwidth usage
    const usage = BandwidthLimitedDownloadService.getCurrentUsage();
    setBandwidthUsage(usage);
    
    // Set up interval to refresh usage every 10 seconds
    const interval = setInterval(() => {
      const updatedUsage = BandwidthLimitedDownloadService.getCurrentUsage();
      setBandwidthUsage(updatedUsage);
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);

  // Helper function to format bytes
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // Handle dark mode toggle
  const handleDarkModeToggle = () => {
    toggleDarkMode();
    showSnackbar(
      !darkMode 
        ? 'Dark mode enabled' 
        : 'Dark mode disabled',
      'success'
    );
  };

  // Handle auto sync toggle
  const handleAutoSyncToggle = () => {
    const newValue = !autoSync;
    setAutoSync(newValue);
    localStorage.setItem('autoSync', JSON.stringify(newValue));
    showSnackbar(
      newValue 
        ? 'Auto-sync enabled. Documents will sync when you go online.' 
        : 'Auto-sync disabled. Documents will not sync automatically.',
      'info'
    );
  };
  
  // Handle auto update toggle
  const handleAutoUpdateToggle = () => {
    const newValue = !autoUpdate;
    setAutoUpdate(newValue);
    localStorage.setItem('autoUpdate', JSON.stringify(newValue));
    showSnackbar(
      newValue 
        ? 'Auto-update enabled. Documents will be updated automatically.' 
        : 'Auto-update disabled. You will be prompted before downloading updates.',
      'info'
    );
  };
  
  // Handle auto caching toggle
  const handleAutoCachingToggle = () => {
    const newValue = !autoCaching;
    setAutoCaching(newValue);
    localStorage.setItem('autoCaching', newValue ? 'true' : 'false');
    
    if (newValue) {
      // Start background caching if newly enabled
      BandwidthLimitedDownloadService.startBackgroundCaching();
      showSnackbar(
        'Auto-caching enabled. Documents will be gradually downloaded in the background (1MB per day limit).',
        'success'
      );
    } else {
      // Clear download queue if disabled
      if (BandwidthLimitedDownloadService.clearQueue) {
        BandwidthLimitedDownloadService.clearQueue();
      }
      showSnackbar(
        'Auto-caching disabled. Only viewed documents will be cached for offline use.',
        'info'
      );
    }
  };

  // Show snackbar helper
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Handle manual sync
  const handleManualSync = async () => {
    if (!navigator.onLine) {
      showSnackbar('Cannot sync while offline', 'error');
      return;
    }

    setSyncInProgress(true);
    try {
      if (onTriggerSync) {
        onTriggerSync(); // This will show the sync progress dialog in App.js
      } else {
        // Fallback if onTriggerSync is not provided
        await DocumentService.syncWithServer();
        showSnackbar('Sync completed successfully', 'success');
      }
    } catch (error) {
      console.error('Sync error:', error);
      showSnackbar('Failed to sync. Please try again.', 'error');
    } finally {
      setSyncInProgress(false);
    }
  };

  // Handle clear cache confirmation
  const handleClearCacheConfirm = () => {
    setShowConfirmDialog(true);
  };

  // Handle actual cache clearing
  const handleClearCache = async () => {
    setShowConfirmDialog(false);
    
    try {
      await DocumentService.clearAllCachedData();
      showSnackbar('All cached data cleared successfully', 'success');
      // Update stats
      setOfflineStats({ documents: 0, sections: 0 });
    } catch (error) {
      console.error('Error clearing cache:', error);
      showSnackbar('Failed to clear cache', 'error');
    }
  };

  // Run storage diagnostics - simplified version of what was in StorageDebugScreen
  const runDiagnostics = async () => {
    setDiagnosticsLoading(true);
    setDiagnosticsError(null);
    
    try {
      // Get storage debug info - this is the core functionality we need from the old screen
      const debugInfo = await DocumentService.getStorageDebugInfo();
      setTestResults(debugInfo);
    } catch (err) {
      console.error('Diagnostics failed:', err);
      setDiagnosticsError(`Failed to run diagnostics: ${err.message}`);
    } finally {
      setDiagnosticsLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            aria-label="back"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: 2 }}>
            Settings
          </Typography>
          <IconButton 
            color="inherit"
            onClick={runDiagnostics}
            disabled={diagnosticsLoading}
          >
            <Refresh />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Display Settings
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <DarkMode />
              </ListItemIcon>
              <ListItemText 
                primary="Dark Mode" 
                secondary="Use dark theme throughout the app"
              />
              <Switch 
                edge="end"
                checked={darkMode}
                onChange={handleDarkModeToggle}
              />
            </ListItem>
          </List>
        </Paper>

        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sync & Storage
          </Typography>
          <List>
            {/* New Auto-Caching Option */}
            <ListItem>
              <ListItemIcon>
                <CloudDownload />
              </ListItemIcon>
              <ListItemText 
                primary="Auto-Caching" 
                secondary="Gradually download documents for offline use (1MB per day)"
              />
              <Switch 
                edge="end"
                checked={autoCaching}
                onChange={handleAutoCachingToggle}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <Refresh />
              </ListItemIcon>
              <ListItemText 
                primary="Auto-Sync" 
                secondary="Automatically sync documents when online"
              />
              <Switch 
                edge="end"
                checked={autoSync}
                onChange={handleAutoSyncToggle}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutline />
              </ListItemIcon>
              <ListItemText 
                primary="Auto-Update" 
                secondary="Automatically download document updates"
              />
              <Switch 
                edge="end"
                checked={autoUpdate}
                onChange={handleAutoUpdateToggle}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <DataUsage />
              </ListItemIcon>
              <ListItemText 
                primary="Manual Sync" 
                secondary="Sync all documents now"
              />
              <Button 
                variant="outlined" 
                color="primary"
                onClick={handleManualSync}
                disabled={!navigator.onLine || syncInProgress}
                startIcon={syncInProgress ? <CircularProgress size={20} /> : <Refresh />}
              >
                {syncInProgress ? 'Syncing...' : 'Sync Now'}
              </Button>
            </ListItem>
            {/* Bandwidth usage display */}
            <Divider />
            <ListItem>
              <ListItemIcon>
                <DataUsage />
              </ListItemIcon>
              <ListItemText 
                primary="Background Download Usage" 
                secondary={`${formatBytes(bandwidthUsage)} / ${formatBytes(bandwidthLimit)} used today`}
              />
              <Box sx={{ width: 100 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={(bandwidthUsage / bandwidthLimit) * 100} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <StorageRounded />
              </ListItemIcon>
              <ListItemText 
                primary="Storage Management" 
                secondary={
                  loading ? 
                  "Loading..." : 
                  `${offlineStats.documents} documents from ${offlineStats.sections} sections available offline`
                }
              />
              <Tooltip title="Clear all cached documents and data">
                <Button 
                  variant="outlined" 
                  color="error"
                  onClick={handleClearCacheConfirm}
                  startIcon={<DeleteForever />}
                  disabled={loading || offlineStats.documents === 0}
                >
                  Clear Cache
                </Button>
              </Tooltip>
            </ListItem>
          </List>
        </Paper>

        {/* Storage Diagnostics Section (moved from StorageDebugScreen) */}
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Diagnostics
          </Typography>
          
          {diagnosticsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {diagnosticsError}
            </Alert>
          )}
          
          {diagnosticsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : testResults ? (
            <>
              {/* Storage Connection Status */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <Storage sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Storage Connection
                    {testResults.firebase && testResults.firebase.success ? (
                      <Chip 
                        icon={<Check />} 
                        label="Connected" 
                        color="success" 
                        size="small" 
                        sx={{ ml: 1 }}
                      />
                    ) : (
                      <Chip 
                        icon={<ErrorIcon />} 
                        label="Failed" 
                        color="error" 
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {testResults.firebase && testResults.firebase.success ? (
                    <>
                      <Typography variant="subtitle2">Root Folders:</Typography>
                      <List dense>
                        {testResults.firebase.prefixes && testResults.firebase.prefixes.length > 0 ? (
                          testResults.firebase.prefixes.map((prefix, idx) => (
                            <ListItem key={idx}>
                              <ListItemText primary={prefix} />
                            </ListItem>
                          ))
                        ) : (
                          <ListItem>
                            <ListItemText primary="No folders found" />
                          </ListItem>
                        )}
                      </List>
                      
                      <Typography variant="subtitle2" sx={{ mt: 2 }}>Root Files:</Typography>
                      <List dense>
                        {testResults.firebase.items && testResults.firebase.items.length > 0 ? (
                          testResults.firebase.items.map((item, idx) => (
                            <ListItem key={idx}>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))
                        ) : (
                          <ListItem>
                            <ListItemText primary="No files found" />
                          </ListItem>
                        )}
                      </List>
                    </>
                  ) : (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {testResults.firebase ? testResults.firebase.error : 'Unknown error'}
                    </Alert>
                  )}
                </AccordionDetails>
              </Accordion>
              
              {/* Browser & Network Status */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Browser & Network Status
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    Online Status: {testResults.navigator.onLine ? 'Online' : 'Offline'}
                  </Typography>
                  <Typography>
                    User Agent: {testResults.navigator.userAgent}
                  </Typography>
                </AccordionDetails>
              </Accordion>
              
              {/* IndexedDB Status */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <Storage sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Database Status
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {testResults.indexedDB && testResults.indexedDB.status === 'connected' ? (
                    <>
                      <Typography>Status: Connected</Typography>
                      <Typography>
                        Cached Documents: {testResults.indexedDB.documentCount}
                      </Typography>
                      <Typography>
                        Synced Sections: {testResults.indexedDB.syncedSections}
                      </Typography>
                      
                      {testResults.indexedDB.sections && testResults.indexedDB.sections.length > 0 && (
                        <>
                          <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            Sections in IndexedDB:
                          </Typography>
                          <List dense>
                            {testResults.indexedDB.sections.map((section, idx) => (
                              <ListItem key={idx}>
                                <ListItemText primary={section} />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}
                    </>
                  ) : (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {testResults.indexedDB ? testResults.indexedDB.error : 'IndexedDB not available'}
                    </Alert>
                  )}
                </AccordionDetails>
              </Accordion>
            </>
          ) : (
            <Button 
              variant="contained" 
              onClick={runDiagnostics} 
              startIcon={<Refresh />}
            >
              Run Diagnostics
            </Button>
          )}
        </Paper>

        <Paper elevation={3} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            About
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Info />
              </ListItemIcon>
              <ListItemText 
                primary="App Version" 
                secondary={appVersion}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <BuildCircle />
              </ListItemIcon>
              <ListItemText 
                primary="Trinity Harper PWA" 
                secondary="© 2025 Trinity Harper. All rights reserved."
              />
            </ListItem>
          </List>
        </Paper>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <DialogTitle>Clear All Cached Data?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete all locally stored documents and data. You'll need an internet connection to access documents again. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleClearCache} color="error" autoFocus>
            Clear All Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsScreen;